import {
	faColumns,
	faFilter,
	faFire,
	faPencil,
	faSearch,
	faSort,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	FilterRulesService,
	preserveDynamicDates,
	SavedSearchQueryService,
	type FilterRule,
	type SavedSearchModel,
} from '@supply-mover-2-0/core';
import { PermissionService } from '@supply-mover-2-0/shared';
import clsx from 'clsx';
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
	type ReactElement,
} from 'react';
import { Tabs } from '../../../components/navigation/Tabs';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { Toast } from '../../../services/ToastService';
import type {
	ColumnConfiguration,
	ListBuilderConfiguration,
	ListConfiguration,
} from '../../types';
import {
	FiltersTabContent,
	type FiltersTabHandle,
	type FiltersTabPendingChanges,
} from '../filters-editor/FiltersTabContent';
import { ColumnConfigTabs } from '../list-configuration/columns/ColumnConfigTabs';
import { SaveNewListModal } from '../saved-search/SaveNewListModal';
import {
	SortTabContent,
	type SortTabHandle,
	type SortTabPendingChanges,
} from '../sort-editor/SortTabContent';

/**
 * Merge the Sort tab's curated `sortOptions` and the Filters tab's overrides
 * into a single listOverrides blob for the saved-search update. Both tabs seed
 * from the same stored listOverrides and touch disjoint keys (filters →
 * showFiltersByDefault, sort → sortOptions), so we start from the filter tab's
 * full view (or the existing stored value when filters weren't touched) and
 * overlay the sort tab's decision. Returns null when the result is empty.
 */
function mergeListOverrides(
	existing: Record<string, unknown> | null,
	filterChanges: FiltersTabPendingChanges | null,
	sortChanges: SortTabPendingChanges | null,
): Record<string, unknown> | null {
	let base: Record<string, unknown> | null =
		filterChanges != null ? filterChanges.listOverrides : existing;

	if (sortChanges != null) {
		const next: Record<string, unknown> = { ...(base ?? {}) };
		if (sortChanges.sortOptions == null) {
			delete next['sortOptions'];
		} else {
			next['sortOptions'] = sortChanges.sortOptions;
		}
		base = Object.keys(next).length > 0 ? next : null;
	}

	return base;
}

// ── Types ────────────────────────────────────────────

interface SidebarItem {
	id: number | string;
	name: string;
	type: 'default' | 'mine' | 'shared';
	canEdit: boolean;
	totalUseCount: number;
	savedSearch: SavedSearchModel | null;
	defaultListConfig: ListConfiguration | null;
}

export interface ColumnEditorHandle {
	saveAll: () => Promise<boolean>;
	openCopyList: () => void;
}

export interface ColumnEditorState {
	isDirty: boolean;
	canSaveDirect: boolean;
	hasPendingChanges: boolean;
}

interface PendingChange {
	columns: Array<ColumnConfiguration>;
	defaultSortId: string | undefined;
}

interface ColumnEditorLayoutProps {
	listKey: string;
	editorLists: Array<ListConfiguration>;
	configuration: ListBuilderConfiguration | undefined;
	activeListId: string | undefined;
	availableFields: Array<unknown>;
	isModalOpen: boolean;
	onUpdateList: (listId: string, updates: Partial<ListConfiguration>) => void;
	onStateChange?: (state: ColumnEditorState) => void;
	onColumnsUpdated?: (
		columns: Array<ColumnConfiguration>,
		defaultSortId?: string,
	) => void;
	/**
	 * Called after a saved search is successfully persisted, with the updated
	 * model. Lets the host re-apply the saved search to the live view so filter /
	 * activeFilter / label-override edits show immediately without a page refresh.
	 */
	onSavedSearchSaved?: (savedSearch: SavedSearchModel) => void;
	initialSelectedSearchId?: string | number;
	activeFilterState?: Record<string, unknown>;
}

// ── Helpers ──────────────────────────────────────────

const TYPE_BADGE_STYLES = {
	default: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
	mine: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300',
	shared:
		'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300',
};

const TYPE_LABELS: Record<string, string> = {
	default: 'Default',
	mine: 'Mine',
	shared: 'Shared',
};

function formatUseCount(count: number): string {
	if (count < 1000) {
		return String(count);
	}
	return `${Math.floor(count / 1000)}k+`;
}

// ── Component ────────────────────────────────────────

export const ColumnEditorLayout = forwardRef<
	ColumnEditorHandle,
	ColumnEditorLayoutProps
>(function ColumnEditorLayout(
	{
		listKey,
		editorLists,
		configuration,
		activeListId,
		availableFields,
		isModalOpen,
		onUpdateList,
		onStateChange,
		onColumnsUpdated,
		onSavedSearchSaved,
		initialSelectedSearchId,
		activeFilterState,
	},
	ref,
) {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// ── State ────────────────────────────────────────
	const [savedSearches, setSavedSearches] = useState<Array<SavedSearchModel>>(
		[],
	);
	const [isLoading, setIsLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedItemId, setSelectedItemId] = useState<string | number | null>(
		null,
	);
	const [workingColumns, setWorkingColumns] = useState<
		Array<ColumnConfiguration>
	>([]);
	const [workingDefaultSortId, setWorkingDefaultSortId] = useState<
		string | undefined
	>(undefined);
	const [isDirty, setIsDirty] = useState(false);
	const [showSaveAsModal, setShowSaveAsModal] = useState(false);
	// listOverrides (showFiltersByDefault + curated sortOptions) captured when
	// "Copy List" is clicked, so the copy inherits the source's curation —
	// including any unsaved Sort/Filters-tab edits in progress.
	const [copyListOverrides, setCopyListOverrides] = useState<
		Record<string, unknown> | undefined
	>(undefined);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editingSavedSearch, setEditingSavedSearch] =
		useState<SavedSearchModel | null>(null);
	const [pendingChanges, setPendingChanges] = useState<
		Map<string | number, PendingChange>
	>(new Map());

	// Right-pane tab switcher: Columns (existing) vs Filters vs Sort.
	const [activeTab, setActiveTab] = useState<'columns' | 'filters' | 'sort'>(
		'columns',
	);

	// Filters tab tracks its own dirty state and exposes a getPendingChanges()
	// imperative handle that handleSaveAll consults to bundle filter edits
	// into the same SavedSearchQueryService.update call as column edits.
	const filtersTabRef = useRef<FiltersTabHandle>(null);
	const [filtersTabDirty, setFiltersTabDirty] = useState(false);

	// Sort tab — same pattern as the Filters tab. Its curated sort options are
	// folded into the saved search's listOverrides; the default it picks flows
	// through workingDefaultSortId (persisted as sortId).
	const sortTabRef = useRef<SortTabHandle>(null);
	const [sortTabDirty, setSortTabDirty] = useState(false);

	const originalColumnsRef = useRef<Array<ColumnConfiguration>>([]);

	// ── Fetch saved searches ─────────────────────────
	const fetchSavedSearches = useCallback(async () => {
		setIsLoading(true);
		const result = await SavedSearchQueryService.list({ listKey });
		if (result.success) {
			setSavedSearches(result.data);
		}
		setIsLoading(false);
	}, [listKey]);

	useEffect(() => {
		if (isModalOpen) {
			void fetchSavedSearches();
		} else {
			// Reset all state when modal closes
			setPendingChanges(new Map());
			setIsDirty(false);
			setSelectedItemId(null);
		}
	}, [isModalOpen, fetchSavedSearches]);

	// ── Build sidebar items ──────────────────────────
	const sidebarItems = useMemo((): Array<SidebarItem> => {
		const items: Array<SidebarItem> = [];
		const seenDefaultIds = new Set<string>();

		// Add default lists from editor configuration
		for (const list of editorLists) {
			seenDefaultIds.add(list.id);

			// Find matching saved search to get totalUseCount
			const matchingSavedSearch = savedSearches.find(
				(ss) => String(ss.id) === list.id && ss.type === 'default',
			);

			items.push({
				id: list.id,
				name: list.name ?? list.id,
				type: 'default',
				canEdit: false,
				totalUseCount: matchingSavedSearch?.totalUseCount ?? 0,
				savedSearch: matchingSavedSearch ?? null,
				defaultListConfig: list,
			});
		}

		// Add saved searches that are not defaults
		for (const ss of savedSearches) {
			if (ss.type === 'default' && seenDefaultIds.has(String(ss.id))) {
				continue;
			}
			if (ss.type === 'default') {
				continue;
			}
			items.push({
				id: ss.id,
				name: ss.name,
				type: ss.type,
				canEdit: ss.canEdit,
				totalUseCount: ss.totalUseCount,
				savedSearch: ss,
				defaultListConfig: null,
			});
		}

		return items;
	}, [editorLists, savedSearches]);

	// ── Auto-select initial item ─────────────────────
	useEffect(() => {
		if (selectedItemId == null && sidebarItems.length > 0 && !isLoading) {
			// Prefer initialSelectedSearchId, then activeListId, then first item
			const initialItem =
				initialSelectedSearchId != null
					? sidebarItems.find(
							(item) =>
								item.id === initialSelectedSearchId ||
								String(item.id) === String(initialSelectedSearchId),
						)
					: undefined;
			const strippedActiveId = activeListId?.startsWith('saved:')
				? activeListId.slice(6)
				: activeListId;
			const activeItem =
				initialItem ??
				sidebarItems.find(
					(item) =>
						String(item.id) === activeListId ||
						String(item.id) === strippedActiveId,
				);
			const itemToSelect = activeItem ?? sidebarItems[0];
			if (itemToSelect != null) {
				selectItem(itemToSelect);
			}
		}
	}, [sidebarItems, activeListId, initialSelectedSearchId, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Get columns for an item ──────────────────────
	const getColumnsForItem = useCallback(
		(item: SidebarItem): Array<ColumnConfiguration> => {
			// Saved search with custom columns
			if (
				item.savedSearch != null &&
				item.savedSearch.columns != null &&
				Array.isArray(item.savedSearch.columns) &&
				item.savedSearch.columns.length > 0
			) {
				return item.savedSearch.columns as Array<ColumnConfiguration>;
			}

			// Default list from config
			if (item.defaultListConfig != null) {
				return item.defaultListConfig.columns ?? [];
			}

			// Saved search without custom columns - fall back to base list
			if (item.savedSearch?.baseListId != null) {
				const baseList = editorLists.find(
					(l) => l.id === item.savedSearch?.baseListId,
				);
				if (baseList != null) {
					return baseList.columns ?? [];
				}
			}

			// Fall back to first editor list's columns
			if (editorLists.length > 0) {
				return editorLists[0]?.columns ?? [];
			}

			return [];
		},
		[editorLists],
	);

	// ── Select item ──────────────────────────────────
	const selectItem = useCallback(
		(item: SidebarItem) => {
			// Preserve current dirty state into pendingChanges before switching
			if (isDirty && selectedItemId != null) {
				setPendingChanges((prev) => {
					const next = new Map(prev);
					next.set(selectedItemId, {
						columns: workingColumns,
						defaultSortId: workingDefaultSortId,
					});
					return next;
				});
			}

			setSelectedItemId(item.id);

			// Check if we have pending changes for the target list
			const pending = pendingChanges.get(item.id);
			if (pending != null) {
				setWorkingColumns(pending.columns);
				setWorkingDefaultSortId(pending.defaultSortId);
				originalColumnsRef.current = getColumnsForItem(item);
				setIsDirty(true);
			} else {
				const cols = getColumnsForItem(item);
				setWorkingColumns(cols);
				setWorkingDefaultSortId(
					item.defaultListConfig?.defaultSortId ??
						item.savedSearch?.sortId ??
						undefined,
				);
				originalColumnsRef.current = cols;
				setIsDirty(false);
			}
		},
		[
			getColumnsForItem,
			isDirty,
			selectedItemId,
			workingColumns,
			workingDefaultSortId,
			pendingChanges,
		],
	);

	// ── Handle list updates from column editor ───────
	const handleUpdateList = useCallback(
		(updates: Partial<ListConfiguration>) => {
			const newColumns = updates.columns ?? workingColumns;
			const newSortId =
				updates.defaultSortId !== undefined
					? updates.defaultSortId
					: workingDefaultSortId;

			if (updates.columns != null) {
				setWorkingColumns(updates.columns);
				setIsDirty(true);
			}
			if (updates.defaultSortId !== undefined) {
				setWorkingDefaultSortId(updates.defaultSortId);
				setIsDirty(true);
			}

			// Keep the pending map in sync for the current item
			if (
				selectedItemId != null &&
				(updates.columns != null || updates.defaultSortId !== undefined)
			) {
				setPendingChanges((prev) => {
					const next = new Map(prev);
					next.set(selectedItemId, {
						columns: newColumns,
						defaultSortId: newSortId,
					});
					return next;
				});
			}
		},
		[selectedItemId, workingColumns, workingDefaultSortId],
	);

	// ── Set default sort from the Sort tab ───────────
	// Separate from handleUpdateList because that uses `!== undefined` to detect
	// presence, which can't express "clear the default" (set it to undefined).
	const handleSetDefaultSort = useCallback(
		(sortId: string | undefined) => {
			setWorkingDefaultSortId(sortId);
			setIsDirty(true);
			if (selectedItemId != null) {
				setPendingChanges((prev) => {
					const next = new Map(prev);
					next.set(selectedItemId, {
						columns: workingColumns,
						defaultSortId: sortId,
					});
					return next;
				});
			}
		},
		[selectedItemId, workingColumns],
	);

	// ── Selected item ────────────────────────────────
	const selectedItem = useMemo(
		() => sidebarItems.find((item) => item.id === selectedItemId) ?? null,
		[sidebarItems, selectedItemId],
	);

	// ── Derive read-only state ───────────────────────
	// A list is read-only if it's a default list or a non-editable saved search
	const isReadOnly =
		selectedItem == null ||
		selectedItem.type === 'default' ||
		!selectedItem.canEdit;

	// ── Working list config for ColumnConfigTabs ─────
	const workingListConfig = useMemo((): ListConfiguration => {
		if (selectedItem?.defaultListConfig != null) {
			return {
				...selectedItem.defaultListConfig,
				columns: workingColumns,
				defaultSortId: workingDefaultSortId,
			};
		}

		// Build from saved search or fallback
		const baseList =
			editorLists.find((l) => l.id === selectedItem?.savedSearch?.baseListId) ??
			editorLists[0];

		return {
			id: String(selectedItem?.id ?? 'working'),
			name: selectedItem?.name ?? '',
			type: 'custom',
			active: true,
			columns: workingColumns,
			defaultSortId: workingDefaultSortId,
			...(baseList != null
				? {
						filterRules: baseList.filterRules,
					}
				: {}),
		};
	}, [selectedItem, workingColumns, workingDefaultSortId, editorLists]);

	// ── Base columns for the Sort tab's available pool ──
	// The full column set (including columns not currently shown) so the Sort tab
	// can offer their sorts even when the column is hidden.
	const baseColumnsForSort = useMemo<Array<ColumnConfiguration>>(() => {
		if (selectedItem?.defaultListConfig != null) {
			return selectedItem.defaultListConfig.columns ?? [];
		}
		const baseList = editorLists.find(
			(l) => l.id === selectedItem?.savedSearch?.baseListId,
		);
		return baseList?.columns ?? workingColumns;
	}, [selectedItem, editorLists, workingColumns]);

	// ── Can save directly ────────────────────────────
	const canSaveDirect =
		selectedItem != null &&
		selectedItem.type !== 'default' &&
		selectedItem.canEdit &&
		selectedItem.savedSearch != null;

	// ── Handle save all pending changes ──────────────
	const handleSaveAll = useCallback(async (): Promise<boolean> => {
		// Build the full set of changes to save, including current dirty state
		const allChanges = new Map(pendingChanges);
		if (isDirty && selectedItemId != null) {
			allChanges.set(selectedItemId, {
				columns: workingColumns,
				defaultSortId: workingDefaultSortId,
			});
		}

		let allSucceeded = true;
		let savedCount = 0;
		const failedNames: Array<string> = [];

		// Save all pending changes for saveable lists
		for (const [itemId, change] of allChanges) {
			const item = sidebarItems.find((i) => i.id === itemId);
			if (item == null) continue;

			// Only save editable saved searches
			const isSaveable =
				item.type !== 'default' && item.canEdit && item.savedSearch != null;
			if (!isSaveable) continue;

			const savedSearchId = item.savedSearch?.id as number;

			// If this is the currently-selected list and the Filters / Sort tabs
			// have unsaved edits, fold them into the same update payload so columns,
			// filters, and sort options persist together (single API round-trip).
			const isSelected = selectedItemId != null && itemId === selectedItemId;
			const filterChanges: FiltersTabPendingChanges | null = isSelected
				? (filtersTabRef.current?.getPendingChanges() ?? null)
				: null;
			const sortChanges: SortTabPendingChanges | null = isSelected
				? (sortTabRef.current?.getPendingChanges() ?? null)
				: null;

			const result = await SavedSearchQueryService.update(savedSearchId, {
				columns: change.columns,
				sortId: change.defaultSortId ?? null,
				...(filterChanges != null
					? {
							activeFilters: filterChanges.activeFilters,
							filterRules: filterChanges.filterRules,
						}
					: {}),
				...(filterChanges != null || sortChanges != null
					? {
							listOverrides: mergeListOverrides(
								item.savedSearch?.listOverrides ?? null,
								filterChanges,
								sortChanges,
							),
						}
					: {}),
			});

			if (result.success) {
				savedCount++;
				// Propagate changes if this is the list currently active in the main view
				if (
					String(itemId) === activeListId ||
					`saved:${itemId}` === activeListId
				) {
					onColumnsUpdated?.(change.columns, change.defaultSortId);
				}
				// Re-apply the saved search to the live view so filter / activeFilter /
				// label-override edits show immediately (no page refresh needed).
				if (result.data != null) {
					onSavedSearchSaved?.(result.data);
				}
			} else {
				allSucceeded = false;
				failedNames.push(item.name);
			}
		}

		// Save filter / sort-only changes on the currently-selected list when
		// columns aren't dirty (the loop above only fires for items with pending
		// column changes).
		const hasColumnChange = allChanges.has(selectedItemId ?? '');
		if (!hasColumnChange && selectedItemId != null) {
			const item = sidebarItems.find((i) => i.id === selectedItemId);
			const filterChanges = filtersTabRef.current?.getPendingChanges() ?? null;
			const sortChanges = sortTabRef.current?.getPendingChanges() ?? null;
			if (
				item != null &&
				item.type !== 'default' &&
				item.canEdit &&
				item.savedSearch != null &&
				(filterChanges != null || sortChanges != null)
			) {
				const savedSearchId = item.savedSearch.id as number;
				const result = await SavedSearchQueryService.update(savedSearchId, {
					...(filterChanges != null
						? {
								activeFilters: filterChanges.activeFilters,
								filterRules: filterChanges.filterRules,
							}
						: {}),
					listOverrides: mergeListOverrides(
						item.savedSearch.listOverrides ?? null,
						filterChanges,
						sortChanges,
					),
				});
				if (result.success) {
					savedCount++;
					// Re-apply to the live view so filter / sort / label-override edits
					// show immediately without a page refresh.
					if (result.data != null) {
						onSavedSearchSaved?.(result.data);
					}
				} else {
					allSucceeded = false;
					failedNames.push(item.name);
				}
			}
		}

		if (savedCount > 0) {
			Toast.show({
				text1:
					savedCount === 1
						? t('Columns saved successfully')
						: t(`${String(savedCount)} lists saved successfully`),
				type: 'success',
			});
		}

		if (failedNames.length > 0) {
			Toast.error(t(`Failed to save: ${failedNames.join(', ')}`));
		}

		// Clear all pending changes and reset dirty state
		setPendingChanges(new Map());
		if (selectedItemId != null) {
			originalColumnsRef.current = workingColumns;
		}
		setIsDirty(false);
		filtersTabRef.current?.reset();
		setFiltersTabDirty(false);
		sortTabRef.current?.reset();
		setSortTabDirty(false);

		// Refresh the saved searches list
		void fetchSavedSearches();

		return allSucceeded;
	}, [
		isDirty,
		selectedItemId,
		pendingChanges,
		workingColumns,
		workingDefaultSortId,
		sidebarItems,
		activeListId,
		t,
		fetchSavedSearches,
		onColumnsUpdated,
		onSavedSearchSaved,
	]);

	// ── Handle copy list (save as new) ──────────────
	const handleSaveAsNew = useCallback(
		(newSavedSearch: SavedSearchModel) => {
			setShowSaveAsModal(false);
			Toast.show({
				text1: t('List copied successfully'),
				type: 'success',
			});
			// Refresh and select the new item — pending changes for other lists are preserved
			void fetchSavedSearches().then(() => {
				setSelectedItemId(newSavedSearch.id);
				originalColumnsRef.current = workingColumns;
				setIsDirty(false);
				// Remove pending entry for the new list since it was just saved
				setPendingChanges((prev) => {
					const next = new Map(prev);
					next.delete(newSavedSearch.id);
					return next;
				});
			});
		},
		[fetchSavedSearches, workingColumns, t],
	);

	// ── Handle edit from sidebar ────────────────────
	const handleEditSearch = useCallback((item: SidebarItem) => {
		if (item.savedSearch != null) {
			setEditingSavedSearch(item.savedSearch);
			setShowEditModal(true);
		}
	}, []);

	const handleEditSave = useCallback(
		(_updatedSearch: SavedSearchModel) => {
			setShowEditModal(false);
			setEditingSavedSearch(null);
			void fetchSavedSearches();
		},
		[fetchSavedSearches],
	);

	const handleEditDelete = useCallback(() => {
		const deletedId = editingSavedSearch?.id;
		setShowEditModal(false);
		setEditingSavedSearch(null);
		void fetchSavedSearches().then(() => {
			// If the deleted item was selected, clear selection so auto-select picks a new one
			if (deletedId != null && selectedItemId === deletedId) {
				setSelectedItemId(null);
			}
		});
	}, [editingSavedSearch, selectedItemId, fetchSavedSearches]);

	// ── Expose imperative handle ─────────────────────
	useImperativeHandle(
		ref,
		() => ({
			saveAll: handleSaveAll,
			openCopyList: () => {
				// Capture the source list's effective overrides (saved + any unsaved
				// Filters/Sort edits) so the new copy starts with the same curation.
				const filterChanges =
					filtersTabRef.current?.getPendingChanges() ?? null;
				const sortChanges = sortTabRef.current?.getPendingChanges() ?? null;
				setCopyListOverrides(
					mergeListOverrides(
						selectedItem?.savedSearch?.listOverrides ?? null,
						filterChanges,
						sortChanges,
					) ?? undefined,
				);
				setShowSaveAsModal(true);
			},
		}),
		[handleSaveAll, selectedItem],
	);

	// ── Report state changes to parent ───────────────
	// hasPendingChanges OR's column changes with filter changes so the modal
	// footer's Apply/OK button enables when either tab has unsaved edits.
	const hasPendingChanges =
		pendingChanges.size > 0 || isDirty || filtersTabDirty || sortTabDirty;
	useEffect(() => {
		onStateChange?.({ isDirty, canSaveDirect, hasPendingChanges });
	}, [isDirty, canSaveDirect, hasPendingChanges, onStateChange]);

	// ── Filter sidebar items by search ───────────────
	const filteredItems = useMemo(() => {
		if (searchQuery.length === 0) {
			return sidebarItems;
		}
		const q = searchQuery.toLowerCase();
		return sidebarItems.filter((item) => item.name.toLowerCase().includes(q));
	}, [sidebarItems, searchQuery]);

	// ── Group items by type ──────────────────────────
	const groupedItems = useMemo(() => {
		const groups: Record<string, Array<SidebarItem>> = {
			default: [],
			mine: [],
			shared: [],
		};
		for (const item of filteredItems) {
			groups[item.type]?.push(item);
		}
		return groups;
	}, [filteredItems]);

	// ── Build base list ID for SaveNewListModal ──────
	const baseListId = useMemo(() => {
		if (selectedItem?.defaultListConfig != null) {
			return selectedItem.defaultListConfig.id;
		}
		if (selectedItem?.savedSearch?.baseListId != null) {
			return selectedItem.savedSearch.baseListId;
		}
		return editorLists[0]?.id ?? undefined;
	}, [selectedItem, editorLists]);

	// Derive filter state from the selected sidebar item — not from the active tab's UI state.
	// This ensures "Save As New" captures the correct filters for the list being cloned,
	// even when the user is viewing a different tab than the one they selected in the sidebar.
	const saveAsFilterState = useMemo((): Record<string, unknown> => {
		// Default lists: derive from filterRules via FilterRulesService which properly
		// unwraps defaultValue into FilterOption arrays. The savedSearch.filterState for
		// default lists stores the raw filterRules format (with defaultValue wrapper)
		// which is not the correct shape for saving a new saved search.
		if (selectedItem?.defaultListConfig != null) {
			const processed = FilterRulesService.process(
				selectedItem.defaultListConfig,
				{ hasPermission: (p) => PermissionService.can(p) },
			);
			// process() resolved the list's dynamic date defaults (e.g. "Last 90
			// Days") to concrete dates for the UI. Restore the {{DATE:...}}
			// placeholders before the copy is persisted so it keeps tracking the
			// relative range instead of freezing at copy time (PD-14159).
			return preserveDynamicDates(
				processed.filterState,
				selectedItem.defaultListConfig,
			);
		}
		// User-created saved searches: use their stored filterState directly
		if (selectedItem?.savedSearch?.filterState != null) {
			return selectedItem.savedSearch.filterState;
		}
		return activeFilterState ?? {};
	}, [selectedItem, activeFilterState]);

	// ── Render ───────────────────────────────────────
	return (
		<div className="flex h-full">
			{/* ─── Sidebar ─────────────────────────── */}
			<div
				className={clsx(
					'w-64 flex flex-col border-r',
					colors.classes.borderDefault,
					colors.classes.bgSecondary,
				)}
			>
				{/* Search */}
				<div className={clsx('p-3 border-b', colors.classes.borderDefault)}>
					<div className="relative">
						<FontAwesomeIcon
							icon={faSearch}
							className={clsx(
								'absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none',
								colors.classes.textMuted,
							)}
						/>
						<input
							type="text"
							value={searchQuery}
							onChange={(e) => {
								setSearchQuery(e.target.value);
							}}
							placeholder={t('Search lists...')}
							className={clsx(
								'w-full pl-9 pr-3 py-2 text-sm border rounded-lg',
								'has-icon-left',
								colors.classes.bgPrimary,
								colors.classes.textPrimary,
								colors.classes.borderDefault,
								'focus:outline-none focus:ring-2 focus:ring-primary-500',
								'placeholder:text-gray-400 dark:placeholder:text-gray-500',
							)}
						/>
					</div>
				</div>

				{/* List items */}
				<div className="flex-1 overflow-y-auto custom-scrollbar">
					{isLoading ? (
						<div className="flex items-center justify-center p-6">
							<span className={clsx('text-sm', colors.classes.textMuted)}>
								{t('Loading...')}
							</span>
						</div>
					) : (
						<>
							{groupedItems['default'] != null &&
								groupedItems['default'].length > 0 && (
									<SidebarSection
										label={t('Default')}
										items={groupedItems['default']}
										selectedItemId={selectedItemId}
										pendingChangeIds={pendingChanges}
										onSelect={selectItem}
										onEdit={handleEditSearch}
									/>
								)}
							{groupedItems['mine'] != null &&
								groupedItems['mine'].length > 0 && (
									<SidebarSection
										label={t('My Lists')}
										items={groupedItems['mine']}
										selectedItemId={selectedItemId}
										pendingChangeIds={pendingChanges}
										onSelect={selectItem}
										onEdit={handleEditSearch}
									/>
								)}
							{groupedItems['shared'] != null &&
								groupedItems['shared'].length > 0 && (
									<SidebarSection
										label={t('Shared')}
										items={groupedItems['shared']}
										selectedItemId={selectedItemId}
										pendingChangeIds={pendingChanges}
										onSelect={selectItem}
										onEdit={handleEditSearch}
									/>
								)}
							{filteredItems.length === 0 && (
								<div className="flex items-center justify-center p-6">
									<span className={clsx('text-sm', colors.classes.textMuted)}>
										{t('No lists found')}
									</span>
								</div>
							)}
						</>
					)}
				</div>
			</div>

			{/* ─── Column Editor ───────────────────── */}
			{/* `min-w-0` keeps the right panel inside its share of the row width.
			    As a `flex-1` child it inherits `min-width: auto` by default, which
			    resolves to the column-editor's min-content — i.e. the widest
			    non-wrappable thing inside (typically the DisplayStyleSelector's
			    custom-component pickers). Without `min-w-0` the panel grows to
			    that min-content, pushes past the modal's edge, and produces a
			    horizontal scrollbar at the bottom of the column-editor area. */}
			<div className="flex-1 min-w-0 flex flex-col">
				{selectedItem != null ? (
					<>
						{/* Header bar with name and status */}
						<div
							className={clsx(
								'px-4 py-3 border-b',
								colors.classes.borderDefault,
								colors.classes.bgSecondary,
							)}
						>
							<div className="flex items-center gap-2">
								<span
									className={clsx(
										'text-sm font-semibold',
										colors.classes.textPrimary,
									)}
								>
									{t(selectedItem.name)}
								</span>
								<span
									className={clsx(
										'px-2 py-0.5 text-xs font-medium rounded',
										TYPE_BADGE_STYLES[selectedItem.type],
									)}
								>
									{t(TYPE_LABELS[selectedItem.type] ?? '')}
								</span>
								{isDirty && (
									<span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
										{t('(unsaved changes)')}
									</span>
								)}
							</div>
							{selectedItem.savedSearch?.description != null &&
								selectedItem.savedSearch.description.length > 0 && (
									<p
										className={clsx(
											'text-xs mt-0.5',
											colors.classes.textSecondary,
										)}
									>
										{t(selectedItem.savedSearch.description)}
									</p>
								)}
						</div>

						{/* Read-only banner for default/non-editable lists */}
						{isReadOnly && (
							<div
								className={clsx(
									'px-4 py-2 border-b text-sm',
									colors.classes.borderDefault,
									'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
								)}
							>
								{t(
									'This is a read-only list. Use "Copy List" to create an editable copy.',
								)}
							</div>
						)}

						{/* Right-pane tab switcher: Columns vs Filters vs Sort */}
						<div
							className={clsx(
								'border-b',
								colors.classes.borderDefault,
								colors.classes.bgPrimary,
								'px-2',
							)}
						>
							<Tabs
								tabs={[
									{ id: 'columns', label: t('Columns'), icon: faColumns },
									{ id: 'filters', label: t('Filters'), icon: faFilter },
									{ id: 'sort', label: t('Sort'), icon: faSort },
								]}
								activeTab={activeTab}
								onTabChange={(id) => {
									setActiveTab(id as 'columns' | 'filters' | 'sort');
								}}
								variant="underline"
								showIcons
							/>
						</div>

						{/* Active tab content */}
						<div className="flex-1 overflow-hidden flex">
							{activeTab === 'columns' && (
								<ColumnConfigTabs
									listConfig={workingListConfig}
									configuration={configuration}
									onUpdateList={handleUpdateList}
									availableFields={availableFields as Array<unknown>}
									isModalOpen={isModalOpen}
									isReadOnly={isReadOnly}
								/>
							)}
							{activeTab === 'filters' && (
								<FiltersTabContent
									ref={filtersTabRef}
									listConfig={workingListConfig}
									configuration={configuration}
									savedSearchOverrides={
										selectedItem?.savedSearch != null
											? {
													activeFilters:
														selectedItem.savedSearch.activeFilters ?? null,
													filterRules:
														(selectedItem.savedSearch.filterRules as Record<
															string,
															FilterRule
														> | null) ?? null,
													listOverrides:
														selectedItem.savedSearch.listOverrides ?? null,
												}
											: null
									}
									isReadOnly={isReadOnly}
									onStateChange={(state) => {
										setFiltersTabDirty(state.hasPendingChanges);
									}}
								/>
							)}
							{activeTab === 'sort' && (
								<SortTabContent
									ref={sortTabRef}
									listConfig={workingListConfig}
									configuration={configuration}
									baseColumns={baseColumnsForSort}
									savedSearchOverrides={
										selectedItem?.savedSearch != null
											? {
													listOverrides:
														selectedItem.savedSearch.listOverrides ?? null,
												}
											: null
									}
									defaultSortId={workingDefaultSortId}
									onSetDefault={handleSetDefaultSort}
									isReadOnly={isReadOnly}
									onStateChange={(state) => {
										setSortTabDirty(state.hasPendingChanges);
									}}
								/>
							)}
						</div>
					</>
				) : (
					<div className="flex-1 flex items-center justify-center p-6">
						<p className={clsx('text-sm', colors.classes.textSecondary)}>
							{t('Select a list to configure columns')}
						</p>
					</div>
				)}
			</div>

			{/* ─── Save As New Modal ───────────────── */}
			<SaveNewListModal
				isOpen={showSaveAsModal}
				onClose={() => {
					setShowSaveAsModal(false);
				}}
				onSave={handleSaveAsNew}
				listKey={listKey}
				filterState={saveAsFilterState}
				sortId={workingDefaultSortId}
				sortFields={selectedItem?.savedSearch?.sortFields ?? undefined}
				columns={workingColumns}
				listOverrides={copyListOverrides}
				baseListId={baseListId}
			/>

			{/* ─── Edit Saved Search Modal ─────────── */}
			<SaveNewListModal
				isOpen={showEditModal}
				onClose={() => {
					setShowEditModal(false);
					setEditingSavedSearch(null);
				}}
				onSave={handleEditSave}
				onDelete={handleEditDelete}
				listKey={listKey}
				filterState={editingSavedSearch?.filterState ?? {}}
				sortId={editingSavedSearch?.sortId}
				sortFields={editingSavedSearch?.sortFields}
				columns={editingSavedSearch?.columns as Array<unknown> | undefined}
				baseListId={editingSavedSearch?.baseListId ?? baseListId}
				editingSavedSearch={editingSavedSearch}
			/>
		</div>
	);
});

// ── Sidebar Section ──────────────────────────────────

function SidebarSection({
	label,
	items,
	selectedItemId,
	pendingChangeIds,
	onSelect,
	onEdit,
}: {
	label: string;
	items: Array<SidebarItem>;
	selectedItemId: string | number | null;
	pendingChangeIds: Map<string | number, PendingChange>;
	onSelect: (item: SidebarItem) => void;
	onEdit?: (item: SidebarItem) => void;
}): ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();

	return (
		<div>
			<div
				className={clsx(
					'px-3 py-2 text-xs font-semibold uppercase tracking-wider',
					colors.classes.textMuted,
				)}
			>
				{label}
			</div>
			{items.map((item) => (
				<button
					key={String(item.id)}
					type="button"
					onClick={() => {
						onSelect(item);
					}}
					className={clsx(
						'w-full flex items-center gap-2 px-3 py-2 text-left',
						'transition-colors duration-150',
						item.id === selectedItemId
							? 'bg-primary-50 dark:bg-gray-800 border-l-2 border-l-primary-500 dark:border-l-primary-400'
							: 'hover:bg-gray-50 dark:hover:bg-gray-700 border-l-2 border-l-transparent',
					)}
				>
					{/* Unsaved changes indicator */}
					{pendingChangeIds.has(item.id) && (
						<span
							className="h-2 w-2 rounded-full bg-amber-500 dark:bg-amber-400 flex-shrink-0"
							title={t('Unsaved changes')}
						/>
					)}
					<span
						className={clsx(
							'flex-1 text-sm font-medium truncate',
							colors.classes.textPrimary,
						)}
					>
						{t(item.name)}
					</span>
					{item.canEdit && item.type !== 'default' && onEdit != null && (
						<span
							role="button"
							tabIndex={-1}
							onClick={(e) => {
								e.stopPropagation();
								onEdit(item);
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									e.stopPropagation();
									onEdit(item);
								}
							}}
							title={t('Edit list settings')}
							className={clsx(
								'p-0.5 rounded text-gray-400 dark:text-gray-500',
								'hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30',
								'transition-colors duration-150',
							)}
						>
							<FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
						</span>
					)}
					{item.totalUseCount > 0 && (
						<div
							className="flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-orange-500 dark:bg-orange-600 text-white rounded"
							title={t('Popularity')}
						>
							<FontAwesomeIcon icon={faFire} className="h-2.5 w-2.5" />
							<span>{formatUseCount(item.totalUseCount)}</span>
						</div>
					)}
					{!item.canEdit && item.type !== 'default' && (
						<span
							className={clsx('text-xs', colors.classes.textMuted)}
							title={t('Read only')}
						>
							{t('RO')}
						</span>
					)}
				</button>
			))}
		</div>
	);
}
